'use client';

import { useTranslations } from 'next-intl';
import { ArrowLeft, Check, Eye, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormFieldRenderer } from './form-field-renderer';
import type { LocalField } from '../types';

interface StepPreviewSaveProps {
  formName: string;
  fields: LocalField[];
  onSave: () => void;
  onPrevious: () => void;
  isSaving: boolean;
  readOnly?: boolean;
  onCancel?: () => void;
}

export function StepPreviewSave({
  formName,
  fields,
  onSave,
  onPrevious,
  isSaving,
  readOnly = false,
  onCancel
}: StepPreviewSaveProps) {
  const t = useTranslations('dynamicForms');

  return (
    <div>
      <div className='mb-6 flex flex-col items-center'>
        <div className='mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30'>
          <Eye className='h-8 w-8 text-amber-600 dark:text-amber-400' />
        </div>
        <h3 className='mb-2 text-lg font-semibold'>{t('previewTitle')}</h3>
        <p className='text-muted-foreground text-sm'>{t('previewHint')}</p>
      </div>

      <div className='bg-muted/30 mx-auto max-w-lg rounded-lg border p-6'>
        <h4 className='mb-6 text-center text-lg font-semibold'>{formName}</h4>
        <div className='space-y-5'>
          {fields.map((field) => (
            <FormFieldRenderer key={field.tempId} field={field} />
          ))}
        </div>
      </div>

      <div className='mt-8 flex items-center justify-between'>
        <Button type='button' variant='outline' onClick={onPrevious}>
          <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
          {t('previous')}
        </Button>
        <div className='flex items-center gap-3'>
          {onCancel && (
            <Button type='button' variant='ghost' onClick={onCancel}>
              <X className='me-2 h-4 w-4' />
              {t('cancel')}
            </Button>
          )}
          <span className='text-muted-foreground text-sm'>
            {t('stepOf', { current: 3, total: 3 })}
          </span>
        </div>
        {!readOnly ? (
          <Button
            type='button'
            className='bg-green-500 hover:bg-green-600'
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            ) : (
              <Check className='me-2 h-4 w-4' />
            )}
            {t('saveForm')}
          </Button>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
}
